import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { BlockPaletteItem } from './types'

export type BlockPaletteProps = {
  items: BlockPaletteItem[]
  onAdd: (type: string) => void
  title?: string
  className?: string
}

/**
 * Sidebar rail of allowed block types. Click to insert into the authoring document.
 * (Drag-from-palette onto canvas can be layered later; click-add is the v1 interaction.)
 */
export function BlockPalette({
  items,
  onAdd,
  title = 'Add block',
  className,
}: BlockPaletteProps) {
  return (
    <aside
      className={cn(
        'flex w-full flex-col gap-2 rounded-md border border-border bg-muted/20 p-3 @md:w-56 @md:shrink-0',
        className,
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.type}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-auto w-full justify-start gap-2 px-2 py-2 text-left whitespace-normal"
              disabled={item.disabled}
              title={item.disabledReason}
              onClick={() => onAdd(item.type)}
            >
              <Plus className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{item.label}</span>
                {item.description ? (
                  <span className="block text-xs font-normal text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
                {item.disabled && item.disabledReason ? (
                  <span className="block text-xs font-normal text-muted-foreground">
                    {item.disabledReason}
                  </span>
                ) : null}
              </span>
            </Button>
          </li>
        ))}
      </ul>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No blocks available to add.</p>
      ) : null}
    </aside>
  )
}
